import React from "react";
import { useRecoilState } from "recoil";
import ScreenCard from "../ScreenCard";
import { newMissionState } from "../../atoms/core";
import MissionCCT from "../../components/editors/MissionCCT";
import { CreateMissionScreensContext } from "../../contexts/CreateMissionManager";
import { useI18n } from "../../hooks/i18n-hooks";
import { Cct, CctDetailsFieldFullValue } from "../../__generated__/graphql";
import { DEFAULT_HOURS_PER_WEEK } from "../../utils";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { Bubble } from "../../components/Bubble";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";

const CreateMissionCCT = () => {
    const i18n = useI18n();
    const [mission, setMission] = useRecoilState(newMissionState);
    const currentProfile = useCurrentProfile();
    const isPrivateClient = currentProfile?.isPrivateClient || false;
    const { validateScreen } = React.useContext(CreateMissionScreensContext);
    const [isValid, setIsValid] = React.useState(false);
    const setCCTId = React.useCallback(
        (value?: Cct) => {
            setMission((oldMission) => {
                let newHoursPerWeek = value?.hoursPerWeek;
                const defaultHoursPerWeek = DEFAULT_HOURS_PER_WEEK;
                // If the hours per week are not set or are greater than the default value,
                // we set the default value
                // If it is lower than we MUST take it as the CCT does not allow default value
                if (!newHoursPerWeek || newHoursPerWeek > defaultHoursPerWeek) {
                    newHoursPerWeek = defaultHoursPerWeek;
                }
                return {
                    ...oldMission,
                    currentContract: {
                        ...oldMission.currentContract,
                        cctEditionId: value?.cctEditionId,
                        cctContractId: value?.cctContractId,
                        cctDetails: { readableValues: [], tempdataCallValues: [] },
                        hoursPerWeekFullTime: newHoursPerWeek,
                    },
                };
            });
        },
        [setMission],
    );
    const setCctDetails = React.useCallback(
        (value: CctDetailsFieldFullValue) => {
            setMission((oldMission) => ({
                ...oldMission,
                currentContract: {
                    ...oldMission.currentContract,
                    cctDetails: value,
                },
            }));
        },
        [setMission],
    );

    const setScreenIsValid = React.useCallback(
        (value: boolean) => {
            setIsValid(value);
        },
        [setIsValid],
    );

    const validate = React.useCallback(() => {
        void validateScreen("CreateMissionCCT");
    }, [validateScreen]);

    const origin = useBubbleStepOrigin("CreateMissionCCT");

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.translate("next"),
                    onPress: validate,
                    isValid: isValid,
                },
            ]}
        >
            <Bubble
                title={
                    !isPrivateClient
                        ? i18n.t("screens.user.create_mission_cct.title")
                        : i18n.t("screens.user.create_mission_cct.private_client_title")
                }
                origin={origin}
            >
                <MissionCCT
                    mission={mission}
                    setCCTId={setCCTId}
                    setCctDetails={setCctDetails}
                    setScreenIsValid={setScreenIsValid}
                ></MissionCCT>
            </Bubble>
        </ScreenCard>
    );
};

export default CreateMissionCCT;
